import {
  action,
  computed,
  observable
} from 'mobx';

import { Paginated } from '../../adl-gen/common';
import { DbKey, WithDbId } from '../../adl-gen/common/db';
import { TableQuery } from '../../adl-gen/common/tabular';
import {
  AppClient,
  AppUser,
  makeUserGroupManager,
  makeUserGroupMember,
  makeUserQuiz,
  UserGroup,
  UserGroupManager,
  UserGroupMember,
  UserQuiz
} from '../../adl-gen/totalinteraction/futurefit/db';
import { UserGroupListEntry } from '../../adl-gen/totalinteraction/futurefit/frontend';
import { GroupData } from '../../adl-gen/totalinteraction/futurefit/requests';
import { fromPromise, MaybeLoading } from '../../loader/loader';
import { Service } from '../../service/service';
import { LoggedIn, LoggedInSuper } from '../../stores/identity-store';

import { DataStore, TransportLayer, UserQuizChange } from './index';

export type UserGroupChange =
  | {
      kind: 'loaded';
      userId: DbKey<AppUser>;
      memberOf?: DbKey<UserGroupMember>;
      managerOf?: DbKey<UserGroupManager>;
      groupId: DbKey<UserGroup>;
    }
  | {
      kind: 'create';
      userId?: DbKey<AppUser>;
      memberOf?: string;
      managerOf?: string;
      groupId: DbKey<UserGroup>;
    }
  | {
      // 'update' in main set; 'create' or 'delete' used when passing to backend
      kind: 'update' | 'delete'; // delete one or both
      userId?: DbKey<AppUser>;
      memberOf?: string | DbKey<UserGroupMember>;
      managerOf?: string | DbKey<UserGroupManager>;
      groupId: DbKey<UserGroup>;
    };

export type GroupUserChange =
  | {
      kind: 'loaded';
      userId: DbKey<AppUser>;
      member?: DbKey<UserGroupMember>;
      manager?: DbKey<UserGroupManager>;
      groupId: DbKey<UserGroup>;
    }
  | {
      kind: 'create';
      userId: DbKey<AppUser>;
      member?: string;
      manager?: string;
      groupId?: DbKey<UserGroup>;
    }
  | {
      kind: 'update' | 'delete';
      userId: DbKey<AppUser>;
      member?: string | DbKey<UserGroupMember>;
      manager?: string | DbKey<UserGroupManager>;
      groupId?: DbKey<UserGroup>;
    };

/// Make UserId required for Quiz changes
export type GroupUserQuizChange = Required<UserQuizChange>;


class UserGroupApi implements TransportLayer<UserGroup> {
  kind: string = 'UserGroup';
  constructor(protected readonly service: Service) {}
  create = async (item: UserGroup): Promise<DbKey<UserGroup>> => this.service.createUserGroup(item);
  update = async (itemWithId: WithDbId<UserGroup>): Promise<void> => this.service.updateUserGroup(itemWithId);
  doDelete = async (id: DbKey<UserGroup>): Promise<void> => this.service.deleteUserGroup(id);
  query = async (q: TableQuery): Promise<Paginated<WithDbId<UserGroup>>> => this.service.queryUserGroups(q);

  queryList = async (appClientId: DbKey<AppClient>): Promise<Paginated<UserGroupListEntry>> =>
    this.service.queryUserGroupList(appClientId);
  queryData = async (appClientId: DbKey<AppClient>): Promise<GroupData> => this.service.queryUserGroupData(appClientId);

  createManager = async (item: UserGroupManager): Promise<DbKey<UserGroupManager>> =>
    this.service.createUserGroupManager(item);
  deleteManager = async (id: DbKey<UserGroupManager>): Promise<void> => this.service.deleteUserGroupManager(id);
  createMember = async (item: UserGroupMember): Promise<DbKey<UserGroupMember>> =>
    this.service.createUserGroupMember(item);
  deleteMember = async (id: DbKey<UserGroupMember>): Promise<void> => this.service.deleteUserGroupMember(id);

  createUserQuiz = async (m: UserQuiz): Promise<DbKey<UserQuiz>> => this.service.createUserQuiz(m);
}

export class UserGroupStore extends DataStore<UserGroup> {
  @observable.ref list: Promise<Paginated<UserGroupListEntry>>;

  @observable clientId: DbKey<AppClient>;
  @observable group?: Promise<UserGroupListEntry>;
  groupData: Promise<GroupData>;

  /// OK - assuming here that we *always* want this to be tied to *either* an AppClientId
  /// *or* a UserGroupId (and therefore indirectly an AppClientId).
  /// Unlike the AppClientStore, which can be an open query, UserGroupStore should
  /// not load unless a client or user is specified.
  public static fromService = (
    service: Service,
    identity: LoggedIn | LoggedInSuper,
    appClientId: DbKey<AppClient>,
    id?: DbKey<UserGroup>
  ): UserGroupStore => {
    const store = new UserGroupStore(new UserGroupApi(service), identity, appClientId, id);
    return store;
  };

  public constructor(
    transport: TransportLayer<UserGroup>,
    identity: LoggedIn | LoggedInSuper,
    appClientId: DbKey<AppClient>,
    id?: DbKey<UserGroup>
  ) {
    super(transport, identity, id);
    this.clientId = appClientId;
    this.data = this.init(id);
    if (id) {
      if (this.authorized) {
        this.group = this.authorized ? this.selectEntry(id) : Promise.reject('Not authorized');
      }
    }
    this.groupData = this.initData();
    this.list = this.initList();
  }

  private readonly initList = () => {
    return this.loadList(this.clientId);
    // Don't call reload() here - or indeed anything else
    // this.data = this.load(c);
  };

  /// TODO: unused
  @action
  public _setGroup = async (userGroupId: DbKey<UserGroup>): Promise<void> => {
    if (this.authorized) {
      this.group = this.selectEntry(userGroupId);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly initData = () => {
    return this.loadData(this.clientId);
  };

  private readonly loadList = async (appClientId: DbKey<AppClient>): Promise<Paginated<UserGroupListEntry>> => {
    if (this.authorizedForClient(appClientId)) {
      const results = await this.queryList(appClientId);
      return results;
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly loadData = async (id: DbKey<AppClient>): Promise<GroupData> => {
    if (this.clientId && this.authorizedForClient(this.clientId)) {
      const data = await this.queryData(id);
      return data;
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly queryList = async (appClientId: DbKey<AppClient>): Promise<Paginated<UserGroupListEntry>> => {
    const results = await (this.transport as UserGroupApi).queryList(appClientId);
    return results;
  };

  private readonly queryData = async (appClientId: DbKey<AppClient>): Promise<GroupData> => {
    const result = await (this.transport as UserGroupApi).queryData(appClientId);
    return result;
  };

  @computed
  public get loadingList(): MaybeLoading<Paginated<UserGroupListEntry>> {
    if (this.authorizedForClient(this.clientId)) {
      return fromPromise(this.list);
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  @computed
  public get loadingData(): MaybeLoading<GroupData> {
    if (this.authorizedForClient(this.clientId)) {
      return fromPromise(this.groupData);
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  @computed
  public get selectedGroup(): MaybeLoading<UserGroupListEntry> | undefined {
    if (this.authorizedForClient(this.clientId)) {
      if (this.group) {
        return fromPromise(this.group);
      } else {
        return undefined;
      }
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  private readonly selectEntry = async (id: DbKey<UserGroup>): Promise<UserGroupListEntry> => {
    const results = await (this.transport as UserGroupApi).queryList(this.clientId);
    // TODO: dereferencing an item that may not exist in the array?
    return results.items.filter(e => e.id === id)[0];
  };


  public updateUserQuizzes = async (updates: GroupUserQuizChange[]): Promise<void> => {
    await Promise.all(
      updates.map(async change => {
        if (change.kind === 'create') {
          await this.createUserQuiz(
            // TODO: DueAt
            makeUserQuiz({
              appUserId: change.userId,
              quizId: change.value,
              startedAt: null,
              completedAt: null,
              dueAt: null
            })
          );
        }
      })
    );
  };

  public updateUserGroups = async (groupId: DbKey<UserGroup>, updates: GroupUserChange[]): Promise<void> => {
    await Promise.all(
      updates.map(async change => {
        if (change.kind === 'create') {
          if (change.manager) {
            await this.createManager(makeUserGroupManager({ appUserId: change.userId, userGroupId: groupId }));
          }
          if (change.member) {
            await this.createMember(makeUserGroupMember({ appUserId: change.userId, userGroupId: groupId }));
          }
        } else if (change.kind === 'delete') {
          if (change.manager) {
            await this.deleteManager(change.manager);
          }
          if (change.member) {
            await this.deleteMember(change.member);
          }
        }
      })
    );
  };

  private readonly createUserQuiz = async (m: UserQuiz): Promise<DbKey<UserQuiz>> => {
    const uqId = await (this.transport as UserGroupApi).createUserQuiz(m);
    return uqId;
  };

  private readonly createManager = async (item: UserGroupManager): Promise<DbKey<UserGroupManager>> => {
    const id = await (this.transport as UserGroupApi).createManager(item);
    return id;
  };

  private readonly deleteManager = async (id: DbKey<UserGroupManager>): Promise<void> => {
    await (this.transport as UserGroupApi).deleteManager(id);
  };

  private readonly createMember = async (item: UserGroupMember): Promise<DbKey<UserGroupMember>> => {
    const id = await (this.transport as UserGroupApi).createMember(item);
    return id;
  };

  private readonly deleteMember = async (id: DbKey<UserGroupMember>): Promise<void> => {
    await (this.transport as UserGroupApi).deleteMember(id);
  };
}



// WEBPACK FOOTER //
// ./src/admin/stores/user-group-store.ts